import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'

import styles from './Button.module.css'

type ButtonSize = 'small' | 'medium'
type ButtonType = 'invisible' | 'default' // | 'primary' | 'danger'

interface BaseButtonProps {
  size?: ButtonSize
  variant?: ButtonType
  condensed?: boolean
  iconOnly?: boolean
  icon?: ReactNode
  disabled?: boolean
  children?: ReactNode
  className?: string
  onClick?: () => void
}

interface ButtonAsButtonProps extends BaseButtonProps {
  type?: 'button' | 'submit' | 'reset'
}

interface ButtonAsLinkProps extends BaseButtonProps {
  href: string
  target?: string
  rel?: string
}

interface ButtonAsRouterLinkProps extends BaseButtonProps {
  to: string
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps | ButtonAsRouterLinkProps

export const Button = ({
  ref,
  size = 'small',
  variant = 'default',
  condensed = false,
  iconOnly = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps & {ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>}) => {
  const classes = clsx(
    styles.button,
    styles[size],
    styles[variant],
    condensed && styles.condensed,
    iconOnly && styles.iconOnly,
    disabled && styles.disabled,
    className,
  )

  const content = (
    <>
      {icon != null ? <span className={styles.icon}>{icon}</span> : null}
      {!iconOnly && children ? <span className={styles.text}>{children}</span> : null}
    </>
  )

  if ('to' in props) {
    const {to, onClick, ...rest} = props
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        to={to}
        className={classes}
        aria-disabled={disabled}
        onClick={onClick}
        {...rest}
      >
        {content}
      </Link>
    )
  }

  if ('href' in props) {
    const {href, target, rel, onClick, ...rest} = props
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={href}
        target={target}
        rel={rel}
        className={classes}
        aria-disabled={disabled}
        onClick={onClick}
        {...rest}
      >
        {content}
      </a>
    )
  }

  const {onClick, type = 'button', ...rest} = props
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {content}
    </button>
  )
}

Button.displayName = 'Button'
